/**
 * SoldierRoster component for managing the soldier list.
 * Enforces composition caps: max 8 soldiers, max 4 specialists.
 * Auto-deducts soldier cost from treasury.
 */

import { useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui';
import { Plus, Users, AlertCircle, Coins } from 'lucide-react';
import { SoldierCard } from './SoldierCard';
import { SoldierTemplatePicker } from './SoldierTemplatePicker';
import {
  useWarbandStore,
  selectSoldiers,
  selectTreasury,
  MAX_SOLDIERS,
  MAX_SPECIALISTS,
  SOLDIER_TEMPLATES,
  type SoldierTemplateId,
  type SoldierMetaPatch,
} from '@/store/warbandSlice';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SoldierRoster() {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Store selectors - only select primitive/stable references
  const soldiers = useWarbandStore(selectSoldiers);
  const treasury = useWarbandStore(selectTreasury);

  // Store actions
  const addSoldier = useWarbandStore((state) => state.addSoldier);
  const updateSoldierMeta = useWarbandStore((state) => state.updateSoldierMeta);
  const removeSoldier = useWarbandStore((state) => state.removeSoldier);

  // Compute derived values with useMemo to avoid infinite loops
  const soldiersWithTemplates = useMemo(() => {
    return soldiers.map((soldier) => ({
      ...soldier,
      template: SOLDIER_TEMPLATES[soldier.templateId],
    }));
  }, [soldiers]);

  const soldierCount = soldiers.length;

  const specialistCount = useMemo(() => {
    return soldiers.filter((s) => {
      const template = SOLDIER_TEMPLATES[s.templateId];
      return template?.isSpecialist ?? false;
    }).length;
  }, [soldiers]);

  const canAddSoldier = soldierCount < MAX_SOLDIERS;
  const canAddSpecialist = specialistCount < MAX_SPECIALISTS;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleAddSoldier = (templateId: SoldierTemplateId) => {
    const result = addSoldier(templateId);
    if (result.success) {
      setIsPickerOpen(false);
    } else {
      alert(result.message);
    }
  };

  const handleUpdateSoldier = (id: string, patch: SoldierMetaPatch) => {
    updateSoldierMeta(id, patch);
  };

  const handleRemoveSoldier = (id: string) => {
    removeSoldier(id);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const remainingSlots = MAX_SOLDIERS - soldierCount;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>Soldiers</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {soldierCount}/{MAX_SOLDIERS}
            </Badge>
            <Badge variant={canAddSpecialist ? 'secondary' : 'destructive'}>
              {specialistCount}/{MAX_SPECIALISTS} specialists
            </Badge>
          </div>
        </div>
        <CardDescription>
          {remainingSlots > 0
            ? `${remainingSlots} slot${remainingSlots !== 1 ? 's' : ''} remaining`
            : 'Roster full'}
          {remainingSlots > 0 && !canAddSpecialist && ' (specialists full)'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Cap Warning */}
        {!canAddSoldier && (
          <div
            className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-4 w-4" />
            Maximum of {MAX_SOLDIERS} soldiers reached.
          </div>
        )}

        {!canAddSpecialist && canAddSoldier && (
          <div
            className="flex items-center gap-2 rounded-md bg-yellow-500/10 p-3 text-sm text-yellow-700 dark:text-yellow-400"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-4 w-4" />
            Maximum of {MAX_SPECIALISTS} specialists reached. You can still add Thugs or
            Thieves.
          </div>
        )}

        {/* Soldier List */}
        {soldiersWithTemplates.length > 0 ? (
          <div className="space-y-2">
            {soldiersWithTemplates.map((soldierWithTemplate) => (
              <SoldierCard
                key={soldierWithTemplate.id}
                soldier={soldierWithTemplate}
                template={soldierWithTemplate.template}
                onUpdate={handleUpdateSoldier}
                onRemove={handleRemoveSoldier}
              />
            ))}
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No soldiers hired yet. Add soldiers to your warband.
          </p>
        )}

        {/* Add Soldier Button */}
        <Sheet open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="w-full"
              disabled={!canAddSoldier}
              onClick={() => setIsPickerOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Hire Soldier
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                Hire Soldier
                <Badge variant="outline" className="font-mono">
                  <Coins className="mr-1 h-3 w-3" />
                  {treasury}gc
                </Badge>
              </SheetTitle>
              <SheetDescription>
                Select a soldier type to add to your warband.
                {!canAddSpecialist && ' Only Thugs and Thieves available (specialist cap reached).'}
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-auto p-4">
              <SoldierTemplatePicker
                onSelect={handleAddSoldier}
                canAddSpecialist={canAddSpecialist}
                availableGold={treasury}
              />
            </div>
          </SheetContent>
        </Sheet>
      </CardContent>
    </Card>
  );
}
